import { useState } from 'react';
import { DateInput } from '../forms';
import { appointmentMinDate } from '../forms/appointmentMinDate';
import { appointmentService, type AppointmentOverview } from '../../services/appointment';
import { toast } from '../../utils/toast';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'other', label: 'Other' },
];

interface EditAppointmentDialogProps {
  appointment: AppointmentOverview['appointment'];
  onClose: () => void;
  onSaved: () => void;
}

/** Change a booked visit's date, time, type, location or reason (until it is recorded). */
export function EditAppointmentDialog({ appointment, onClose, onSaved }: EditAppointmentDialogProps) {
  const [scheduledDate, setScheduledDate] = useState(appointment.scheduledDate);
  const [dateValid, setDateValid] = useState(true);
  const [type, setType] = useState(appointment.type);
  const [location, setLocation] = useState(appointment.location);
  const [chiefComplaint, setChiefComplaint] = useState(appointment.chiefComplaint ?? '');
  const [saving, setSaving] = useState(false);
  const canSave = dateValid && !!location.trim() && !saving;

  const save = async () => {
    try {
      setSaving(true);
      await appointmentService.updateAppointment(appointment.id, {
        appointmentDetails: { scheduledDate, type, location: location.trim(), chiefComplaint: chiefComplaint.trim() },
      } as never);
      toast.success('Appointment updated');
      onSaved();
      onClose();
    } catch {
      // The API client shows the reason (for example, the visit was recorded meanwhile).
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit appointment</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <DateInput
            label="Date & time"
            value={scheduledDate}
            onChange={setScheduledDate}
            onValidationChange={setDateValid}
            required
            showTime
            // Bookings from earlier days stay editable (a no-show's location, say): never require a
            // date later than the one already stored.
            minDate={new Date(Math.min(new Date(appointment.scheduledDate).getTime(), new Date(appointmentMinDate()).getTime())).toISOString()}
          />
          <div>
            <label htmlFor="edit-appointment-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select id="edit-appointment-type" className="input-field" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="edit-appointment-location" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input id="edit-appointment-location" className="input-field" value={location} maxLength={255} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div>
            <label htmlFor="edit-appointment-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason for visit</label>
            <textarea id="edit-appointment-reason" className="input-field" rows={3} value={chiefComplaint} maxLength={500} onChange={(e) => setChiefComplaint(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={save} disabled={!canSave}>{saving ? 'Saving...' : 'Save'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
